import { CartUpdatedEvent } from '@theme/events';
import {
  buildGiftLineProperties,
  registerGiftConfig,
  unregisterGiftConfig,
  getRefused,
  setRefused,
  setGiftInCart,
  reconcileState,
  openNextPendingPopup,
  claimPopup,
  releasePopup,
} from '@theme/auto-gift-processor';

class AutoGift extends HTMLElement {
  constructor() {
    super();

    this.blockId = null;
    this.triggerType = 'cart_value';
    this.threshold = 0;
    this.quantityPerProduct = 1;
    this.collectionHandle = '';
    this.needsVariantChoice = false;
    this.giftBadge = '';
    this.products = [];

    this.dialog = null;
    this.dialogModal = null;
    this.confirmButton = null;

    this.popupOpen = false;
    this.popupValidated = false;

    this.confirmHandler = this.handleConfirm.bind(this);
    this.dialogCloseHandler = this.handleDialogClose.bind(this);
    this.variantSelectHandler = this.handleVariantSelectChange.bind(this);
    this.exclusiveChoiceHandler = this.handleExclusiveChoice.bind(this);
    this.cardSelectHandler = this.handleCardSelect.bind(this);
    this.cardClickHandler = this.handleCardClick.bind(this);
  }

  connectedCallback() {
    this.blockId = this.dataset.blockId;
    this.triggerType = this.dataset.triggerType || 'cart_value';
    this.threshold = parseFloat(this.dataset.threshold) || 0;
    this.quantityPerProduct = parseInt(this.dataset.quantityPerProduct, 10) || 1;
    this.collectionHandle = this.dataset.collectionHandle || '';
    this.subscriptionOnly = this.dataset.subscriptionOnly === 'true';
    this.needsVariantChoice = this.dataset.needsVariantChoice === 'true';
    this.choiceMode = this.dataset.choiceMode === 'single' ? 'single' : 'all';

    if (!this.#parseConfig()) return;

    this.dialog = this.querySelector('dialog-component');
    this.dialogModal = this.querySelector('[data-ref="popup-modal"]');
    this.confirmButton = this.querySelector('[data-ref="gift-confirm"]');

    this.confirmButton?.addEventListener('click', this.confirmHandler);
    this.dialogModal?.addEventListener('close', this.dialogCloseHandler);

    this.querySelectorAll('[data-ref="gift-variant-select"]').forEach((select) => {
      select.addEventListener('change', this.variantSelectHandler);
    });

    if (this.choiceMode === 'single') {
      this.querySelectorAll('[data-ref="gift-product-checkbox"]').forEach((checkbox) => {
        checkbox.addEventListener('change', this.exclusiveChoiceHandler);
      });

      this.querySelectorAll('[data-ref="gift-select"]').forEach((button) => {
        button.addEventListener('click', this.cardSelectHandler);
      });

      this.querySelectorAll('[data-ref="gift-product"]').forEach((card) => {
        card.addEventListener('click', this.cardClickHandler);
      });
    }

    registerGiftConfig(
      this.blockId,
      {
        triggerType: this.triggerType,
        threshold: this.threshold,
        quantityPerProduct: this.quantityPerProduct,
        collectionHandle: this.collectionHandle,
        subscriptionOnly: this.subscriptionOnly,
        needsVariantChoice: this.needsVariantChoice,
        giftBadge: this.giftBadge,
        products: this.products,
      },
      this,
    );

    this.#reconcileInitialState();

    if (this.needsVariantChoice) {
      queueMicrotask(() => {
        if (this.isConnected) this.#evaluateForPopup();
      });
    }
  }

  disconnectedCallback() {
    // L'ajout au panier re-rend la section : l'élément est retiré du DOM avant
    // que « close » ne se déclenche, donc handleDialogClose ne libère jamais le
    // verrou et plus aucune popup ne peut s'ouvrir ensuite.
    releasePopup(this.blockId);
    unregisterGiftConfig(this.blockId, this);
    this.confirmButton?.removeEventListener('click', this.confirmHandler);
    this.dialogModal?.removeEventListener('close', this.dialogCloseHandler);

    this.querySelectorAll('[data-ref="gift-variant-select"]').forEach((select) => {
      select.removeEventListener('change', this.variantSelectHandler);
    });

    this.querySelectorAll('[data-ref="gift-product-checkbox"]').forEach((checkbox) => {
      checkbox.removeEventListener('change', this.exclusiveChoiceHandler);
    });

    this.querySelectorAll('[data-ref="gift-select"]').forEach((button) => {
      button.removeEventListener('click', this.cardSelectHandler);
    });

    this.querySelectorAll('[data-ref="gift-product"]').forEach((card) => {
      card.removeEventListener('click', this.cardClickHandler);
    });
  }

  #parseConfig() {
    const configEl = this.querySelector('[data-ref="gift-config"]');
    if (!configEl) return false;

    try {
      const config = JSON.parse(configEl.textContent);
      this.giftBadge = config.giftBadge || '';
      this.products = config.products || [];
      return this.products.length > 0;
    } catch (error) {
      console.error('AutoGift: invalid config', error);
      return false;
    }
  }

  #reconcileInitialState() {
    const initialAbove = this.dataset.initialAboveThreshold === 'true';
    const initialGiftInCart = this.dataset.initialGiftInCart === 'true';

    reconcileState(this.blockId, initialAbove, initialGiftInCart);
  }

  #evaluateForPopup() {
    const above = this.dataset.initialAboveThreshold === 'true';
    const giftInCart = this.dataset.initialGiftInCart === 'true';
    const refused = getRefused(this.blockId);

    if (above && !giftInCart && !refused) {
      this.#openPopup();
    }
  }

  openPopupIfNeeded() {
    if (this.popupOpen) return;
    if (getRefused(this.blockId)) return;
    this.#openPopup();
  }

  #openPopup() {
    if (!this.dialog || !this.isConnected) return;
    // une autre popup cadeau est déjà ouverte : on attend son tour
    if (!claimPopup(this.blockId, this)) return;

    this.popupOpen = true;
    this.popupValidated = false;

    const checkboxes = [...this.querySelectorAll('[data-ref="gift-product-checkbox"]')];

    checkboxes.forEach((checkbox) => {
      // En mode « un seul au choix », rien n'est pré-sélectionné : c'est le bouton
      // de la carte cliquée qui coche puis valide
      checkbox.checked = this.choiceMode !== 'single';
    });

    this.dialog.open();
  }

  handleCardClick(event) {
    // le bouton et le sélecteur de variante gèrent déjà leur propre clic
    if (event.target.closest('[data-ref="gift-select"]')) return;
    if (event.target.closest('[data-ref="gift-variant-select"]')) return;
    if (event.target.closest('label')) return;

    const button = event.currentTarget.querySelector('[data-ref="gift-select"]');
    if (button && !button.classList.contains('is-loading')) button.click();
  }

  handleCardSelect(event) {
    const button = event.currentTarget;
    const productEl = button.closest('[data-ref="gift-product"]');
    const checkbox = productEl?.querySelector('[data-ref="gift-product-checkbox"]');
    if (!checkbox) return;

    this.querySelectorAll('[data-ref="gift-product-checkbox"]').forEach((other) => {
      other.checked = other === checkbox;
    });

    this.handleConfirm(event, button);
  }

  async handleConfirm(event, button = this.confirmButton) {
    event?.preventDefault();
    this.popupValidated = true;

    const variantCounts = new Map();

    for (const productEl of this.querySelectorAll('[data-ref="gift-product"]')) {
      const checkbox = productEl.querySelector('[data-ref="gift-product-checkbox"]');
      if (!checkbox?.checked) continue;

      const productId = productEl.dataset.productId;
      const product = this.products.find((p) => String(p.id) === String(productId));
      if (!product) continue;

      const select = productEl.querySelector('[data-ref="gift-variant-select"]');
      const variantId = select ? parseInt(select.value, 10) : product.defaultVariantId;

      variantCounts.set(variantId, (variantCounts.get(variantId) || 0) + 1);
    }

    if (variantCounts.size === 0) {
      setRefused(this.blockId, true);
      this.dialog?.close();
      return;
    }

    const items = [];

    for (const [variantId, quantity] of variantCounts) {
      items.push({
        id: variantId,
        quantity,
        properties: buildGiftLineProperties(this.blockId, this.giftBadge),
      });
    }

    button?.classList.add('is-loading');

    try {
      await this.#addItemsToCart(items);
    } finally {
      button?.classList.remove('is-loading');
      this.dialog?.close();
    }
  }

  handleDialogClose() {
    const wasRefused = this.popupOpen && !this.popupValidated;

    if (wasRefused) {
      setRefused(this.blockId, true);
    }

    this.popupOpen = false;
    this.popupValidated = false;
    releasePopup(this.blockId);

    // Le verrou vient d'être libéré : c'est le seul moment sûr pour ouvrir le
    // palier suivant, qu'on vienne d'un choix validé ou d'un refus.
    queueMicrotask(() => openNextPendingPopup());
  }

  handleExclusiveChoice(event) {
    const changed = event.currentTarget;
    if (!changed.checked) return;

    this.querySelectorAll('[data-ref="gift-product-checkbox"]').forEach((checkbox) => {
      if (checkbox !== changed) checkbox.checked = false;
    });
  }

  handleVariantSelectChange(event) {
    const select = event.currentTarget;
    const selectedOption = select.options[select.selectedIndex];
    const newImage = selectedOption.dataset.image;
    if (!newImage) return;

    const productEl = select.closest('[data-ref="gift-product"]');
    const imageEl = productEl?.querySelector('[data-ref="gift-product-image"]');
    if (imageEl) imageEl.src = newImage;
  }

  async #addItemsToCart(items) {
    if (!this.isConnected) return;

    const sections = ['auto-gifts'];
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.dataset.sectionId) sections.push(cartDrawer.dataset.sectionId);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(',') }),
      });

      const data = await response.json();

      if (!this.isConnected) return;

      if (data.status >= 400 || data.errors) {
        console.warn('AutoGift: failed to add gift items', data);
        return;
      }

      setGiftInCart(this.blockId, true);

      if (window.location.pathname.includes('/cart')) {
        window.location.reload();
        return;
      }

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: data.item_count,
            source: 'auto-gift-popup',
            sections: data.sections,
          },
        ),
      );
    } catch (error) {
      console.error('AutoGift: failed to add gift items', error);
    }
  }
}

if (!customElements.get('auto-gift')) {
  customElements.define('auto-gift', AutoGift);
}
